// Vocabulários editáveis pelo usuário: função de Funcionário, categoria de
// Insumo, tipo de Cliente, Processo de peça (ADR-0006), Motivo de Retenção
// (ADR-0007) e Motivo de Divergência (ADR-0021, ADR-0022). Funções puras
// sobre o Snapshot.

import type { Listas, Snapshot } from "./snapshot.ts";

export type NomeLista = keyof Listas;

export class ErroLista extends Error {}

/**
 * Motivo de Retenção do sistema (ADR-0033): a Taxa de Relavagem Interna do
 * Dashboard conta só ele, então não pode ser renomeado nem excluído — senão o
 * indicador zeraria sem aviso.
 */
export const MOTIVO_RELAVAGEM = "Relavagem";

/** Valor que a lista sempre tem e que o usuário não renomeia nem exclui. */
export function valorDoSistema(lista: NomeLista, valor: string): boolean {
  return lista === "motivosRetencao" && valor === MOTIVO_RELAVAGEM;
}

const ERRO_RELAVAGEM_FIXO = `"${MOTIVO_RELAVAGEM}" é motivo do sistema: não pode ser renomeado nem excluído.`;

export function adicionarValor(
  snapshot: Snapshot,
  lista: NomeLista,
  valor: string,
): Snapshot {
  const v = valor.trim();
  if (v === "") throw new ErroLista("Informe um valor.");
  const atuais = snapshot.listas[lista];
  if (atuais.some((x) => x.toLowerCase() === v.toLowerCase())) {
    throw new ErroLista(`"${v}" já está na lista.`);
  }
  return {
    ...snapshot,
    listas: { ...snapshot.listas, [lista]: [...atuais, v] },
  };
}

export function removerValor(
  snapshot: Snapshot,
  lista: NomeLista,
  valor: string,
): Snapshot {
  if (valorDoSistema(lista, valor)) throw new ErroLista(ERRO_RELAVAGEM_FIXO);
  return {
    ...snapshot,
    listas: {
      ...snapshot.listas,
      [lista]: snapshot.listas[lista].filter((x) => x !== valor),
    },
  };
}

/** Um valor renomeado no "Gerenciar": `de` (nome gravado) → `para` (nome novo). */
export interface Renomeio {
  de: string;
  para: string;
}

/**
 * Troca `de` por `para` em todo registro que guarda o valor desta lista.
 * O valor do vocabulário É a identidade gravada (não há id) — renomear só
 * na lista deixaria os registros com o nome velho, e no Motivo de Retenção
 * isso partiria a linha de saldo do Backlog em duas (a chave do saldo é o
 * motivo, em retenção, baixa e Perda).
 */
function renomearNasReferencias(s: Snapshot, lista: NomeLista, de: string, para: string): Snapshot {
  const t = (v: string): string => (v === de ? para : v);
  switch (lista) {
    case "funcoesFuncionario":
      return { ...s, funcionarios: s.funcionarios.map((x) => ({ ...x, funcao: t(x.funcao) })) };
    case "categoriasInsumo":
      return { ...s, insumos: s.insumos.map((x) => ({ ...x, categoria: t(x.categoria) })) };
    case "unidadesInsumo":
      return {
        ...s,
        insumos: s.insumos.map((x) => ({ ...x, unidade: t(x.unidade) })),
        contas: s.contas.map((c) => ({ ...c, itens: c.itens.map((i) => ({ ...i, unidade: t(i.unidade) })) })),
      };
    case "tiposCliente":
      return { ...s, clientes: s.clientes.map((x) => ({ ...x, tipo: t(x.tipo) })) };
    case "processosPeca":
      return { ...s, tiposPeca: s.tiposPeca.map((x) => ({ ...x, processos: x.processos.map(t) })) };
    case "motivosRetencao":
      return {
        ...s,
        retencoes: s.retencoes.map((x) => ({ ...x, motivo: t(x.motivo) })),
        baixasBacklog: s.baixasBacklog.map((x) => ({ ...x, motivo: t(x.motivo) })),
        // a Perda do Backlog Inicial usa o motivo fixo dele, não um da lista
        perdas: s.perdas.map((x) => (x.origemLoteId == null ? x : { ...x, motivoOrigem: t(x.motivoOrigem) })),
      };
    case "motivosDivergencia":
      return {
        ...s,
        contagens: s.contagens.map((x) =>
          x.conferidaMotivo == null ? x : { ...x, conferidaMotivo: t(x.conferidaMotivo) },
        ),
        perdas: s.perdas.map((x) => ({ ...x, motivo: t(x.motivo) })),
      };
    default: {
      const nunca: never = lista;
      return nunca;
    }
  }
}

/**
 * Substitui a lista inteira (validando: sem vazios, sem duplicatas). Os
 * `renomeados` trocam o nome também em todo registro que já o usa — tudo
 * numa operação só, para lista e registros nunca discordarem.
 */
export function definirLista(
  snapshot: Snapshot,
  lista: NomeLista,
  valores: readonly string[],
  renomeados: readonly Renomeio[] = [],
): Snapshot {
  const novos = new Set(valores.map((v) => v.trim()));
  let comRenomes = snapshot;
  for (const r of renomeados) {
    const de = r.de;
    const para = r.para.trim();
    if (para === "") throw new ErroLista("Informe o novo nome.");
    if (valorDoSistema(lista, de) && para !== de) throw new ErroLista(ERRO_RELAVAGEM_FIXO);
    if (!snapshot.listas[lista].includes(de)) {
      throw new ErroLista(`"${de}" não está mais na lista.`);
    }
    if (!novos.has(para)) {
      throw new ErroLista(`"${para}" precisa estar na lista para renomear "${de}".`);
    }
    if (de !== para) comRenomes = renomearNasReferencias(comRenomes, lista, de, para);
  }
  const limpos: string[] = [];
  for (const raw of valores) {
    const v = raw.trim();
    if (v === "") continue;
    if (limpos.some((x) => x.toLowerCase() === v.toLowerCase())) {
      throw new ErroLista(`"${v}" está duplicado na lista.`);
    }
    limpos.push(v);
  }
  for (const fixo of snapshot.listas[lista].filter((v) => valorDoSistema(lista, v))) {
    if (!limpos.includes(fixo)) throw new ErroLista(ERRO_RELAVAGEM_FIXO);
  }
  return { ...comRenomes, listas: { ...comRenomes.listas, [lista]: limpos } };
}

/**
 * Semeadura do ADR-0033: garante "Relavagem" nos Motivos de Retenção. Se a
 * lista tem o motivo com outra caixa ("relavagem"), troca o nome também nos
 * registros (o valor é a identidade gravada); se não tem, acrescenta. Devolve o
 * MESMO objeto quando não há nada a fazer.
 */
export function garantirMotivoRelavagem(snapshot: Snapshot): Snapshot {
  const lista = snapshot.listas.motivosRetencao;
  if (lista.includes(MOTIVO_RELAVAGEM)) return snapshot;
  const outraCaixa = lista.find((v) => v.toLowerCase() === MOTIVO_RELAVAGEM.toLowerCase());
  if (outraCaixa != null) {
    const s = renomearNasReferencias(snapshot, "motivosRetencao", outraCaixa, MOTIVO_RELAVAGEM);
    return {
      ...s,
      listas: { ...s.listas, motivosRetencao: lista.map((v) => (v === outraCaixa ? MOTIVO_RELAVAGEM : v)) },
    };
  }
  return { ...snapshot, listas: { ...snapshot.listas, motivosRetencao: [MOTIVO_RELAVAGEM, ...lista] } };
}

export function listasPadrao(): Listas {
  return {
    funcoesFuncionario: ["Lavador", "Motorista", "Passadeira", "Outro"],
    categoriasInsumo: ["Químico", "Embalagem", "Gás"],
    unidadesInsumo: ["kg", "L", "unidade", "cilindro", "pacote", "caixa"],
    tiposCliente: ["Hotel", "Pousada", "Resort", "Airbnb", "Sauna"],
    processosPeca: [
      "Lavar à máquina",
      "Lavar à mão",
      "Secar na secadora",
      "Secar natural",
      "Passar",
      "Dobrar",
    ],
    motivosRetencao: [
      MOTIVO_RELAVAGEM,
      "Não terminou de passar",
      "Não terminou de dobrar",
    ],
    motivosDivergencia: [
      "Erro de contagem do cliente",
      "Perdida",
      "Danificada",
    ],
  };
}
